import type {
  BriefSolution,
  DraftValidation,
  Project,
  ProjectTimeline,
  V2Asset,
} from "@/types";
import { api } from "@/services/api";

const json = (body: unknown): RequestInit => ({
  method: "PATCH",
  body: JSON.stringify(body),
});

export const v2 = {
  currentSolution: (briefId: number) =>
    api<{ solution: BriefSolution | null }>(
      `/v2/briefs/${briefId}/solutions/current`,
    ),
  confirmSolution: (briefId: number, solutionId: number) =>
    api<{ solution: BriefSolution; project: Project }>(
      `/v2/briefs/${briefId}/solutions/${solutionId}/confirm`,
      { method: "POST" },
    ),
  draft: (briefId: number) =>
    api<{ project: Project; validation: DraftValidation }>(
      `/v2/briefs/${briefId}/draft`,
    ),
  updateDraft: (briefId: number, body: unknown) =>
    api<{ project: Project }>(`/v2/briefs/${briefId}/draft`, json(body)),
  addTask: (briefId: number, body: unknown) =>
    api<{ project: Project; taskId: number }>(
      `/v2/briefs/${briefId}/draft/tasks`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  updateTask: (briefId: number, taskId: number, body: unknown) =>
    api<{ project: Project }>(
      `/v2/briefs/${briefId}/draft/tasks/${taskId}`,
      json(body),
    ),
  deleteTask: (briefId: number, taskId: number) =>
    api<{ project: Project }>(
      `/v2/briefs/${briefId}/draft/tasks/${taskId}`,
      { method: "DELETE" },
    ),
  addChecklist: (taskId: number, body: unknown) =>
    api<{ project: Project }>(`/v2/tasks/${taskId}/checklist-items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateChecklist: (itemId: number, body: unknown) =>
    api<{ project: Project }>(`/v2/checklist-items/${itemId}`, json(body)),
  deleteChecklist: (itemId: number) =>
    api<{ project: Project }>(`/v2/checklist-items/${itemId}`, {
      method: "DELETE",
    }),
  submit: (briefId: number) =>
    api<{ project: Project }>(`/v2/briefs/${briefId}/submit`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
    }),
  dashboard: () =>
    api<{
      projects: Array<{
        id: number;
        title: string;
        status: string;
        lifecycleState: string;
        updatedAt: string;
        progress: Project["progress"];
      }>;
      counts: Record<string, number>;
    }>("/v2/client/dashboard"),
  timeline: (briefId: number) =>
    api<{ timeline: ProjectTimeline }>(`/v2/briefs/${briefId}/timeline`),
  teamProjects: () =>
    api<{ projects: Array<Project & { progress: Project["progress"] }>; counts: Record<string, number> }>(
      "/v2/team/projects",
    ),
  teamProject: (briefId: number) =>
    api<{ project: Project; timeline: ProjectTimeline }>(
      `/v2/team/projects/${briefId}`,
    ),
  acceptProject: (briefId: number) =>
    api<{ project: Project }>(`/v2/team/projects/${briefId}/accept`, {
      method: "POST",
    }),
  scheduleTask: (taskId: number, body: unknown) =>
    api<{ project: Project }>(
      `/v2/team/tasks/${taskId}/schedule`,
      json(body),
    ),
  updateChecklistStatus: (itemId: number, body: unknown) =>
    api<{ project: Project }>(
      `/v2/team/checklist-items/${itemId}/status`,
      json(body),
    ),
  prepareChangeImpact: (changeId: number, body: unknown) =>
    api<{ project: Project }>(
      `/v2/team/change-requests/${changeId}/impact`,
      json(body),
    ),
  decideChange: (changeId: number, decision: "approved" | "rejected") =>
    api<{ project: Project }>(`/v2/change-requests/${changeId}/decision`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    }),
  uploadConversationAsset: (briefId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api<{ asset: V2Asset }>(
      `/v2/briefs/${briefId}/conversation-assets`,
      { method: "POST", body: form },
    );
  },
  uploadTaskAsset: (taskId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api<{ asset: V2Asset }>(`/v2/tasks/${taskId}/assets`, {
      method: "POST",
      body: form,
    });
  },
  linkAssetToTask: (assetId: number, taskId: number) =>
    api<{ assets: V2Asset[] }>(`/v2/assets/${assetId}/links`, {
      method: "POST",
      body: JSON.stringify({ taskId }),
    }),
};
